import {ObservationData} from "./observation.model";

export class HealthPlanData {
  readonly type: string = "healthPlan";
  id: number;
  subject: number;
  insertTime: Date;
  updateTime: Date;
  replacesId: number;
  statusCodeId: number;
  periodStart: Date;
  periodEnd: Date;
  authorId: number;
  diagnosesId: number;
  responsibleId: number;
  reasonCancelled: string;
  personId: number;
  notes: string;
}

export class HealthPlanTasks {
  readonly type: string = "task";
  id?: number;
  healthPlanId?: number;
  typeId?: number;
  meetingId?: number;
  scheduled?: Date;
  description?: string;
  performerId?: number;
  isDone?: boolean;
  visibleToPatient: boolean;
  insertTime?: Date;
  updateTime?: Date;
  isDoneDate?: Date;
  isHealthPlan?: boolean;
  periodStart?: Date;
  periodEnd?: Date;
}

export class HealthPlanActivities {
  readonly type: string = "healthPlan";
  id: number;
  healthPlanId: number;
  personId: number;
  typeId: number;
  description: string;
  repeatPeriodId: number;
  repeatFrequency: number;
  repeatDuration: string;
  insertTime: Date;
  updateTime: Date;
  reportsList?: HealthPlanActivitiesReport[];
  observationList?: ObservationData[];
  progress: Progress[];
  weekProgress: Progress;
}

export class Progress {
  name: string;
  value: number;
}

export class HealthPlanActivitiesReport {
  readonly type: string = "activityReport";
  id?: number;
  activityId?: number;
  repeatValue?: number;
  durationValue?: string;
  effectiveTime?: Date;
  insertTime?: Date;
  updateTime?: Date;
  personId?: number;
}

export class HealthPlanGoals {
  readonly type: string = "goal";
  id: number;
  healthPlanId: number;
  description: string;
  typeId: number;
  insertTime: Date;
  updateTime: Date;
  conditionId: number;
  duration: number;
  targets?: HealthPlanGoalTargets[] = [];
  radius?: number;
  percent?: number;
  status?: string;
  obsTypeName?: string;
  hasBeenOnTrack?: string;
  ofTime?: string;
  timeString?: string;
  effectivePeriod?: string;
  personId?: number;
}

export class HealthPlanGoalTargets {
  readonly type: string = "target";
  id: number;
  goalId: number;
  measureId: number;
  conditionId: number;
  detailRangeMin: string;
  detailRangeMax: string;
  detailValue: string;
  insertTime: Date;
  updateTime: Date;
  valueUnitId: number;
  personId?: number;
  hasMaxValue?: number;
}

export class MedicationStatement {
  readonly type: string = "medicationStatement";
  id: number;
  healthPlanId?: number;
  status?: string;
  informationSourcePractitioner?: number;
  derivedFrom?: string;
  medicationId?: string;
  insertTime?: Date;
  updateTime?: Date;
  personId?: number;
  medicationStatement?: MedicationStatement[];
}

export interface MedicationRequest {
  prescriptionId?: number;
  status?: number;
  personId?: number;
  insertTime?: Date;
  updateTime?: Date;
  authoredOnDate?: Date;
  validUntilDate?: Date;
  ingredientAndStrength?: string;
  cureDosageJson?: string;
  cureFormName?: string;
  diagnosis?: string;
  recipeAuthorName?: string;
  recipeAuthorSpecialityCode?: string;
  cureMedicationName?: string;
  dispensedMedicationName?: string;
  medicationCode?: string;
  cureAtcCode?: string;
  atcName?: string;
  dispensedDate?: Date
}

export class BasePrescription {
  readonly type: string = "medicationRequest";
  id: number;
  basePrescriptionId: string;
  cureAtcCode: string;
  atcName: string;
  ingredientAndStrength: string;
  authoredDate: string;
  medications: MedicationRequest[];
  cureDosageJson?: string;
  totalRepeatCount: number;
  dispensedCount: number;
  remainingCount: number;
  computedStatus: string;
}

export class OverTheCounterMedication {
  readonly type: string = "medication";
  id: number;
  name?: string;
  ingredientAndStrength?: string;
  dose?: string;
  dosageInfo?: string;
  insertTime?: Date;
  updateTime?: Date;
}

// Not used?
export class MedicationRequest_MedicationStatement {
  readonly type: string = "medicationRequest_MedicationStatement";
  id: number;
  medicationStatementId?: number;
  medicationRequestId?: number;
  insertTime?: Date;
  updateTime?: Date;
}

export class CureDosageJson {
  ravikuuriTyyp: string;
  ravikuuriPikkus: number;
  tykke: number;
  tykkeYhik: string;
  kordi: number;
  ajayhik: string;
  hommik?: string;
  louna?: string;
  ohtu?: string;
  oo?: string;
  periood?: string;
  lisaread: Lisaread;
}

export interface Lisaread {
  item?: (null)[] | null;
}

export class TaskHealthPlan {
  doNotChangeId: number;
  healthPlanId: number;
  id: number;
  insertTime: string;
  personId: number;
  taskId: number;
  type: string;
}

export class MedicationItem {
  public id: number;
  public prescriptionId: string;
  public recipeAuthorName: string;
  public recipeAuthorSpecialityCode: string;
  public status: string;
  public statusEnum: string;
  public atcCode: string;
  public atcName: string;
  public formCode: string;
  public authoredOnDate: string;
  public authoredOnDateObj: Date;
  public validUntilDate: string;
  public validUntilDateObj: Date;
  public diagnosis: string;
  public cureAtcCode: string;
  public cureMedicationName: string;
  public cureFormName: string;
  public dispensedMedicationName: string;
  public ingredientAndStrength: string;
  public medicationCode: string;
  public cureDosageJson: string;
  public dosageInfo: string;
  public medicationInfo: string;
  public medicationName: string;
  public insertTime: string;
  public type: string;
  public personId: number;
}

export enum MinMaxValues {
  diastolicMin = 60,
  diastolicMax = 120,
  systolicMin = 90,
  systolicMax = 180,
  weightMin = 30,
  weightMax = 300,
  heightMin = 100,
  heightMax = 300,
  bmiMin = 14,
  bmiMax = 49.5,
  pefMin = 300,
  pefMax = 700,
  bSugarMin = 3,
  bSugarMax = 15,
  minutesMin = 0,
  minutesMax = 59,
  hoursMin = 0,
  hoursMax = 23,
  cigarettesMax = 99,
  activeDaysPerWeek = 7,
  activityTimesPerWeek = 20,
  sleepMaxHours = 24,
  enoughAerobicMinutes = 150,
  enoughEverydayMoveMinutes = 180,
  rateMax = 10,
  fieldMinimum = 0,
  heightUpperLimit = 250,
  weightUpperLimit = 400
}
